import React, { useState, useCallback, useEffect } from 'react';
import {
  Paper,
  Box,
  Button,
  Stack,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useBuild } from '../contexts/BuildContext';
import { useGlobal } from '../contexts/GlobalContext';
import SortingStep from './SortingStep';
import DiversityStep from './DiversityStep';
import LimitsByFieldStep from './LimitsByFieldStep';
import { getAvailableData } from '../features_utils';


const RankingQueryColumn = () => {
  const { mergeCurrentContext } = useGlobal();
  const { hits, setHits, rankingApi, isLoading } = useBuild();

  const { features, scores } = getAvailableData(hits);
  const availableFields = [...features, ...scores];

  // Each step reports { enabled, config }; parent only stores that.
  const [sortStep, setSortStep] = useState({ enabled: false, config: null });
  const [diversityStep, setDiversityStep] = useState({ enabled: false, config: null });
  const [limitsByFieldStep, setLimitsByFieldStep] = useState({ enabled: false, config: null });

  useEffect(() => {
    mergeCurrentContext({
      rankingSortEnabled: sortStep.enabled,
      rankingDiversityEnabled: diversityStep.enabled,
      rankingLimitsEnabled: limitsByFieldStep.enabled,
    });
    return () => mergeCurrentContext({
      rankingSortEnabled: undefined,
      rankingDiversityEnabled: undefined,
      rankingLimitsEnabled: undefined,
    });
  }, [sortStep.enabled, diversityStep.enabled, limitsByFieldStep.enabled, mergeCurrentContext]);

  const getUsefulFieldsAndEmbedding = useCallback(() => {
    const useful = new Set();
    const sortConfig = sortStep.config;
    if (sortConfig?.params) {
      if (Array.isArray(sortConfig.params.fields)) {
        sortConfig.params.fields.forEach((f) => useful.add(f));
      }
      if (Array.isArray(sortConfig.params)) {
        sortConfig.params.forEach((p) => p.field && useful.add(p.field));
      }
    }
    const diversityConfig = diversityStep.config;
    if (diversityConfig?.method === 'fields' && diversityConfig.params?.fields) {
      diversityConfig.params.fields.forEach((f) => useful.add(f));
    }
    const limitsConfig = limitsByFieldStep.config;
    if (limitsConfig?.rules) {
      limitsConfig.rules.forEach((r) => r.field && useful.add(r.field));
    }
    const needEmbedding = diversityConfig?.method === 'semantic';
    return { usefulFields: useful, needEmbedding };
  }, [sortStep.config, diversityStep.config, limitsByFieldStep.config]);

  const handleExecuteQuery = async () => {
    if (!hits || hits.length === 0) return;
    const sortConfig = sortStep.config;
    const diversityConfig = diversityStep.config;
    const limitsByFieldConfig = limitsByFieldStep.config;
    const { usefulFields, needEmbedding } = getUsefulFieldsAndEmbedding();

    const items = hits.map((hit) => {
      const item = { item_id: hit._id };
      for (const key of usefulFields) {
        const v = hit._features?.[key] ?? hit._scores?.[key];
        if (v !== undefined) item[key] = v;
      }
      if (needEmbedding) {
        let embed = hit._source?.item_sem_embed2;
        if (!embed || !Array.isArray(embed)) {
          embed = hit._source?.text_vector;
        }
        item.embed = embed;
        if (!embed) {
          console.log(hit);
        }
      }
      return item;
    });

    const payload = {
      items,
      sort: sortConfig || undefined,
      diversity: diversityConfig || undefined,
      limits_by_field: limitsByFieldConfig || undefined,
    };

    try {
      const result = await rankingApi.rankFeed(payload);
      if (result?.error) {
        console.error('Ranking API error', result.error);
        return;
      }
      const resultItems = result?.result?.items || [];
      const scoreByItemId = {};
      resultItems.forEach(({ item_id, score }) => {
        scoreByItemId[item_id] = score;
      });
      for (const hit of hits) {
        hit._ranking_score = scoreByItemId[hit._id] ;
      }
      setHits([...hits]);
    } catch (err) {
      console.error('Error running ranking', err);
    }
  };

  // Valid when: each step is either disabled or has a valid config
  const isStepValid = ({ enabled, config }) => !enabled || config != null;
  const isQueryValid =
    hits.length > 0 &&
    isStepValid(sortStep) &&
    isStepValid(diversityStep) &&
    isStepValid(limitsByFieldStep);

  return (
    <Paper elevation={2} sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Compose Query
      </Typography>

      <Stack spacing={2} sx={{ flex: 1, overflow: 'auto' }}>
        <SortingStep
          onConfigChange={setSortStep}
          availableFields={availableFields}
        />
        <DiversityStep
          onConfigChange={setDiversityStep}
          availableFields={availableFields}
        />
        <LimitsByFieldStep
          onConfigChange={setLimitsByFieldStep}
          availableFields={availableFields}
        />

        <Box sx={{ mt: 'auto', pt: 1 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExecuteQuery}
            disabled={!isQueryValid || isLoading}
            fullWidth
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Executing...
              </>
            ) : (
              'Execute Query'
            )}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};

export default RankingQueryColumn;
